// InviteHandover — the ONE handover screen for setup links, shared by the create
// wizard and the grant/resend/reset door: what was SENT where (green ✓ rows —
// this is a SUCCESS moment, never an alarm), and what the admin still hands over
// (the copy field, its purpose said in calm helper text). Couriers that did NOT
// run are simply absent — the link's framing carries the action instead
// ("send it through a channel you trust"). Warning tone is reserved for a real
// problem (the chat mint failed).

import { useTranslation } from 'react-i18next';
import type { WebInvite } from '@ems-app/schemas/users/responses';
import type { ExternalIdentityInvite } from '@ems-app/schemas/users/telegram';
import { Modal } from '../../components/ui/Modal.js';
import { Button } from '../../components/ui/Button.js';
import { CopyField } from '../../components/ui/CopyField.js';
import { Alert } from '../../components/ui/Alert.js';
import { icons } from '../../components/ui/icons.js';
import { formatDate } from '../../lib/format.js';
import { useOptionalProjectParams } from '../../hooks/useProjectParams.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';

export interface InviteHandoverData {
  name: string;
  /** The email the invitation was addressed to (shown on the sent row when known). */
  email: string | null;
  /** The web setup link payload (null = no web invitation in this act). */
  web: WebInvite | null;
  /** The chat invite minted alongside (create only; null = not asked / failed). */
  chat: ExternalIdentityInvite | null;
  /** The chat square was ticked but the mint failed — say so, never block. */
  chatFailed: boolean;
  /** The web square was ticked but the invitation mint failed AFTER the account
   *  was created — say so; the grant door on their page is the retry. */
  webFailed: boolean;
}

export interface InviteHandoverProps {
  open: boolean;
  /** The dialog title — the caller names the moment ("Account created — X",
   *  "Invitation for X", "Reset link for X"). */
  title: string;
  data: InviteHandoverData | null;
  onDone: () => void;
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">{children}</p>
  );
}

function SentRow({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-center gap-1.5 text-sm text-[var(--color-text-primary)]">
      <icons.statusSuccess size={16} aria-hidden className="shrink-0 text-[var(--color-success)]" />
      {children}
    </p>
  );
}

export function InviteHandover({ open, title, data, onDone }: InviteHandoverProps) {
  const { t } = useTranslation('users');
  // The screen's one zone (ADR-082 §2): the project's where the handover shows.
  const projectId = useOptionalProjectParams();
  const display = useDisplayZone(projectId ? { kind: 'project', projectId } : { kind: 'person' });
  const zone = display.zone ?? display.viewerZone ?? 'UTC';
  if (!data) return null;
  const emailed = data.web !== null && data.web.email_sent;
  return (
    <Modal
      open={open}
      onClose={onDone}
      title={title}
      maxWidth="max-w-lg"
      footer={
        <Button variant="primary" onClick={onDone}>
          {t('handover.done')}
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        {data.web && (
          <div className="flex flex-col gap-3">
            <SectionHeading>{t('handover.webHeading')}</SectionHeading>
            {emailed && (
              <SentRow>{data.email ? t('handover.emailedTo', { email: data.email }) : t('handover.emailedGeneric')}</SentRow>
            )}
            {data.web.telegram_sent && <SentRow>{t('handover.chatSentRow')}</SentRow>}
            <CopyField label={t('handover.linkLabel')} value={data.web.link} secret />
            <p className="text-xs text-[var(--color-text-secondary)]">
              {emailed || data.web.telegram_sent ? t('handover.linkBackupHelp') : t('handover.linkOnlyHelp')}
            </p>
          </div>
        )}

        {data.chat && (
          <div className="flex flex-col gap-3">
            <SectionHeading>{t('handover.chatHeading')}</SectionHeading>
            <CopyField label={t('handover.chatLinkLabel')} value={data.chat.deep_link} />
            <CopyField label={t('handover.chatCodeLabel')} value={data.chat.short_code} />
            <p className="text-xs text-[var(--color-text-secondary)]">
              {t('handover.chatHelp', {
                bot: data.chat.bot_username,
                when: formatDate(data.chat.expires_at, 'date', { timeZone: zone }) ?? '',
              })}
            </p>
          </div>
        )}

        {data.chatFailed && (
          <div className="flex flex-col gap-3">
            <SectionHeading>{t('handover.chatHeading')}</SectionHeading>
            <Alert variant="warning">{t('handover.chatFailed')}</Alert>
          </div>
        )}

        {data.webFailed && (
          <div className="flex flex-col gap-3">
            <SectionHeading>{t('handover.webHeading')}</SectionHeading>
            <Alert variant="warning">{t('handover.webFailed')}</Alert>
          </div>
        )}
      </div>
    </Modal>
  );
}
